import React, { createContext, useState, useEffect, useCallback } from 'react';
import config from '../config';

export const CityContext = createContext();

export const CityProvider = ({ children }) => {
    const [cities, setCities] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isServerDown, setIsServerDown] = useState(false);

    const getFallbackCities = () => {
        // Liste de villes principales du Congo en cas d'échec de l'API
        return [
            { id: 1, name: 'Kinshasa' },
            { id: 2, name: 'Lubumbashi' },
            { id: 3, name: 'Mbuji-Mayi' },
            { id: 4, name: 'Kisangani' },
            { id: 5, name: 'Masina' },
            { id: 6, name: 'Kananga' },
            { id: 7, name: 'Likasi' },
            { id: 8, name: 'Kolwezi' },
            { id: 9, name: 'Tshikapa' },
            { id: 10, name: 'Bukavu' },
            { id: 11, name: 'Mwene-Ditu' },
            { id: 12, name: 'Kikwit' },
            { id: 13, name: 'Mbandaka' },
            { id: 14, name: 'Matadi' },
            { id: 15, name: 'Uvira' },
            { id: 16, name: 'Boma' },
            { id: 17, name: 'Butembo' },
            { id: 18, name: 'Gandajika' },
            { id: 19, name: 'Kalemie' },
            { id: 20, name: 'Goma' }
        ];
    };

    const fetchCitiesWithRetry = useCallback(async (retries = 3) => {
        for (let i = 0; i < retries; i++) {
            try {
                const response = await fetch(`${config.backendUrl}/users/cities/`, {
                    timeout: 10000
                });
                
                if (response.ok) {
                    const data = await response.json();
                    return data;
                }
            } catch (error) {
                console.error(`Tentative ${i + 1} de chargement des villes échouée:`, error);
                
                if (i === retries - 1) {
                    // Dernière tentative échouée, utiliser les villes de secours

                    return getFallbackCities();
                }
                
                // Attendre avant de réessayer (1s, 2s, 3s)
                await new Promise(resolve => setTimeout(resolve, 1000 * (i + 1)));
            }
        }
    }, []);

    const fetchCities = useCallback(async () => {
        try {
            setIsLoading(true);
            setIsServerDown(false);

            // Essayer de charger depuis localStorage d'abord
            const storedCities = localStorage.getItem('cities');
            const storedTimestamp = localStorage.getItem('cities_timestamp');
            
            // Utiliser les villes en cache si elles ont moins de 24h
            if (storedCities && storedTimestamp) {
                const isStale = Date.now() - parseInt(storedTimestamp) > 24 * 60 * 60 * 1000;
                if (!isStale) {
                    try {
                        setCities(JSON.parse(storedCities));
                        setIsLoading(false);
                        return;
                    } catch (error) {
                        console.error('Error parsing cached cities:', error);
                        localStorage.removeItem('cities');
                        localStorage.removeItem('cities_timestamp');
                        // Continue to fetch fresh data
                    }
                }
            }

            // Charger depuis le backend avec retry
            const data = await fetchCitiesWithRetry();

            // Mettre à jour state et localStorage
            setCities(data);
            localStorage.setItem('cities', JSON.stringify(data));
            localStorage.setItem('cities_timestamp', Date.now().toString());
            
        } catch (error) {
            console.error('Échec final du chargement des villes:', error);
            
            // Essayer localStorage même si expiré
            const storedCities = localStorage.getItem('cities');
            if (storedCities) {

                try {
                    setCities(JSON.parse(storedCities));
                } catch (parseError) {
                    console.error('Error parsing expired cache:', parseError);
                    localStorage.removeItem('cities');
                    setCities(getFallbackCities());
                }
            } else {
                // Utiliser les villes de secours

                setCities(getFallbackCities());
            }
            
            setIsServerDown(true);
        } finally {
            setIsLoading(false);
        }
    }, [fetchCitiesWithRetry]);

    useEffect(() => {
        fetchCities();
    }, [fetchCities]);

    return (
        <CityContext.Provider value={{ cities, isLoading, isServerDown }}>
            {children}
        </CityContext.Provider>
    );
};